import * as React from 'react';

import { Box, Typography, Card, TypographyProps, LinearProgress } from '@mui/material';

import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';

import {
    FaOptionIcon,
    fileDocIcon,
    fileIcon,
    fileJpgIcon,
    filePdfIcon,
    filePngIcon,
    fileXlsIcon,
    openIcon,
    validateIcon,
} from '@europrocurement/l2d-icons';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { ItemActions } from '../../molecules/ItemActions/ItemActions';
import { useFakeProgressBar } from '../../../hooks';
import { useL2DTheme } from '../../../hooks/useL2DTheme';

type handleClickProps = {
    url: string;
    nom: string;
};

export type currentUploadStatus = 'idle' | 'pending' | 'done' | 'error';

export type DisplayFileProps = {
    url?: string; // nom, poid , mimetype ( changer l'icone en fonction du mimetype )
    nom: string;
    status?: currentUploadStatus;
    color?: TypographyProps['color'];
    backgroundColor?: TypographyProps['color'];
    size?: number;
    mimetype?: string;
    onDelete?: () => void;
    onEdit?: (file: File) => void;
    handleClick?: (props: handleClickProps) => void;
    mode?: 'inline' | 'card';
    styleSize?: 'small' | 'medium' | 'large';
};

export const extensionToMimeType = (ext: string): string => {
    switch (ext) {
        case '.pdf':
            return 'application/pdf';
        case '.jpg':
        case '.jpeg':
            return 'image/jpeg';
        case '.png':
            return 'image/png';

        case '.doc':
        case '.docx':
        case '.odt':
            return 'application/msword';

        case '.xls':
        case '.xlsx':
        case '.ods':
            return 'application/vnd.ms-excel';

        default:
            return '';
    }
};

export const mimetypeToIcone = (mimetype: string): FontAwesomeIconProps['icon'] => {
    switch (mimetype) {
        case 'application/pdf':
            return filePdfIcon.props.icon;
        case 'image/jpg':
        case 'image/jpeg':
            return fileJpgIcon.props.icon;
        case 'image/png':
            return filePngIcon.props.icon;

        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        case 'application/vnd.oasis.opendocument.text':
        case 'application/msword':
            return fileDocIcon.props.icon;

        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        case 'application/vnd.oasis.opendocument.spreadsheet':
        case 'application/vnd.ms-excel':
            return fileXlsIcon.props.icon;

        default:
            return fileIcon.props.icon;
    }
};

export const statusToColor = (status: currentUploadStatus): TypographyProps['color'] => {
    switch (status) {
        case 'done':
            return 'success.main';
        case 'error':
            return 'error.main';
        default:
            return 'blueGrey.main';
    }
};

/**
 * Convertisseur de taille de fichier d'octets vers Mb
 *
 * @deprecated utiliser plutôt formatFileSize qui utilise l'unité la plus cohérente
 * @param size la taille du fichier en octets
 * @returns la taille en Mb
 */
export const sizeInMB = (size: number): string => (size / (1024 * 1024)).toFixed(2);

/**
 * Convertisseur de taille de fichier d'octets vers l'unité la plus proche
 *
 * @param size la taille du fichier en octets
 * @returns la taille avec l'unité correspondante sous forme de chaîne de caractère.
 */
export const formatFileSize = (sizeInBytes: number): string => {
    if (sizeInBytes === 0) {
        return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const base = 1024;

    // Calculer l'index de l'unité appropriée
    const unitIndex = Math.floor(Math.log(sizeInBytes) / Math.log(base));

    // S'assurer qu'on ne dépasse pas les unités disponibles
    const finalUnitIndex = Math.min(unitIndex, units.length - 1);

    // Calculer la valeur dans l'unité appropriée
    const size = sizeInBytes / base ** finalUnitIndex;

    // Formater avec le bon nombre de décimales
    let formattedSize: string;

    if (size >= 100) {
        // Pour les valeurs >= 100, pas de décimales
        formattedSize = size.toFixed(0);
    } else if (size >= 10) {
        // Pour les valeurs >= 10, une décimale
        formattedSize = size.toFixed(1);
    } else {
        // Pour les valeurs < 10, deux décimales
        formattedSize = size.toFixed(2);
    }

    formattedSize = formattedSize.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');

    return `${formattedSize} ${units[finalUnitIndex]}`;
};

/**
 *
 * url :
 *      tester que l'url est la bonne
 *
 * icone :
 *      tester que le changement d'icone marche
 *
 * couleur :
 *      tester que le changement de couleur marche
 *
 */

export const DisplayFile: React.FunctionComponent<DisplayFileProps> = function ({
    url,
    size,
    nom,
    mimetype = '',
    status = 'done',
    onDelete,
    onEdit,
    handleClick,
    color,
    backgroundColor,
    mode = 'card',
    styleSize = 'medium',
}) {
    const { progress, progressBuffer, started } = useFakeProgressBar();

    // refaire le compo en 3 box :2 grid 1 de 2 box une autre d'une
    // 1 box logo
    // 1 box flex vertical textes + taille + barre ( voir ce qu'on fait si on a bare + taille )
    // 1 box 2 icones

    const { palette } = useL2DTheme();

    const statusToIconToken = (s: currentUploadStatus) => {
        switch (s) {
            case 'done':
                return palette.icon?.success;
            case 'error':
                return palette.icon?.error;
            case 'pending':
                return palette.icon?.info;
            default:
                return palette.icon?.secondary;
        }
    };

    const token = statusToIconToken(status);
    const resolvedBgColor = backgroundColor ?? token?.backgroundColor;
    const resolvedIconColor = color ?? token?.textColor;

    // Tailles basées sur la propriété styleSize
    const buttonSizes = {
        small: { height: 32, paddingLeft: 2, icon: 'sm' as SizeProp },
        medium: { height: 64, paddingLeft: 4, icon: '1x' as SizeProp },
        large: { height: 96, paddingLeft: 6, icon: 'lg' as SizeProp },
    };

    const sizeConfig = buttonSizes[styleSize];

    const compo = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                height: `${sizeConfig.height}px`,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 2,
                    }}
                >
                    {mode === 'inline' ? (
                        <Box
                            data-testid="test-id-status-color-box"
                            sx={{
                                backgroundColor: palette.icon?.primary?.backgroundColor,
                                padding: '8px',
                                borderRadius: '10px',
                                fontSize: '14px',
                                margin: '14px',
                                height: 36,
                                width: 36,
                            }}
                        >
                            <FaOptionIcon
                                data-testid="test-id-mimetype-icone"
                                icon={mimetypeToIcone(mimetype)}
                                sx={{ color: palette.icon?.primary?.textColor }}
                            />
                        </Box>
                    ) : (
                        <Box
                            data-testid="test-id-status-color-box"
                            sx={{
                                backgroundColor: resolvedBgColor,
                                padding: '8px',
                                borderRadius: '10px',
                                fontSize: '25px',
                            }}
                        >
                            <FaOptionIcon
                                data-testid="test-id-mimetype-icone"
                                icon={mimetypeToIcone(mimetype)}
                                sx={{ color: resolvedIconColor }}
                                size={sizeConfig.icon}
                            />
                        </Box>
                    )}

                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                    >
                        <Box>
                            <Typography
                                variant="l2dcaption"
                                color="primary.dark"
                            >
                                {nom}

                                {handleClick ? (
                                    <Box
                                        data-testid="test-id-handleClick-button"
                                        display="inline-block"
                                        role="button"
                                        onClick={() => {
                                            if (handleClick && url) {
                                                handleClick({ nom, url });
                                            }
                                        }}
                                        sx={{
                                            fontSize: '10px',
                                            marginLeft: '10px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <FaOptionIcon
                                            {...openIcon.props}
                                            size={sizeConfig.icon}
                                        />
                                    </Box>
                                ) : null}
                            </Typography>
                        </Box>

                        <Box>
                            {size && status !== 'pending' ? (
                                <Box data-testid="test-id-sizeFile">
                                    <Typography
                                        variant="l2dbodyxs"
                                        color="text.gris70"
                                    >
                                        {formatFileSize(size)}
                                        <Box
                                            sx={{ mt: '10px', ml: '10px', display: 'inline-block' }}
                                        >
                                            <FaOptionIcon
                                                icon={validateIcon.props.icon}
                                                size="sm"
                                            />
                                        </Box>
                                    </Typography>
                                </Box>
                            ) : null}
                            {status === 'pending' || started ? (
                                <LinearProgress
                                    data-testid="test-id-progressbar"
                                    variant="buffer"
                                    color={progress < 100 ? 'primary' : 'success'}
                                    value={progress}
                                    valueBuffer={progressBuffer}
                                />
                            ) : null}
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Box
                sx={{
                    marginTop: 'auto',
                    marginBottom: 'auto',
                    display: 'flex',
                    gap: '16px',
                    flexDirection: 'row',
                }}
            >
                <ItemActions
                    editTooltip="Modifier le document"
                    deleteTooltip="Supprimer le document"
                    onEdit={
                        onEdit
                            ? () => {
                                  // onEdit();
                              }
                            : undefined
                    }
                    onDelete={
                        onDelete
                            ? () => {
                                  onDelete();
                              }
                            : undefined
                    }
                    sx={{
                        scale: '1.25',
                        marginRight: '20px',
                    }}
                />
            </Box>
        </Box>
    );

    return mode === 'card' ? <Card sx={{ padding: '8px', margin: 0 }}>{compo}</Card> : compo;
};
